import React, { useState, useEffect, useMemo } from "react";
import { Map, Marker, InfoWindow, GoogleApiWrapper } from "google-maps-react";
import { config } from "../../Utility/Constants";
import { Icon } from "@iconify-icon/react";
import { Card } from "../ui/card";

type LatLng = { lat: number; lng: number };

type ContractorLocationInnerProps = { google?: any; user: any };

const ContractorLocationInner: React.FC<ContractorLocationInnerProps> = (
  props,
) => {
  const [selected, setSelected] = useState<LatLng | null>(null);
  const [location, setLocation] = useState<LatLng | null>(null);
  const [zoom] = useState(13);

  // google-maps-react Marker onClick signature: (props, marker, e)
  const onMarkerClick = (props: any, marker: any, e: any) => {
    // Use marker.position or props.position
    if (marker && marker.position) {
      setSelected({ lat: marker.position.lat(), lng: marker.position.lng() });
    } else if (props && props.position) {
      setSelected(props.position);
    }
  };

  const onCloseInfoWindow = () => {
    setSelected(null);
  };

  // Build address from user profile
  const address: string = useMemo(() => {
    const parts = [props.user?.Address, props.user?.Zip].filter(Boolean);
    return parts.join(" ").trim();
  }, [props.user?.Address, props.user?.Zip]);

  // Geocode the address once the Google Maps script is loaded
  useEffect(() => {
    let cancelled = false;
    if (!address || !address.trim()) return;
    if (!window.google || !window.google.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (cancelled) return;
      if (status === "OK" && results && results[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        const latlng = { lat: loc.lat(), lng: loc.lng() } as LatLng;
        setLocation(latlng);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [address]);

  const mapsLink = useMemo(() => {
    if (location) {
      return `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`;
    }
    if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return "#";
  }, [location, address]);

  // No children in Map/InfoWindow
  return (
    <div className="contractor-map-container">
      <Map
        google={props.google}
        className="contractor-map"
        center={location || { lat: 0, lng: 0 }}
        initialCenter={{ lat: 0, lng: 0 }}
        zoom={location ? zoom : 2}
      >
        {location && <Marker position={location} onClick={onMarkerClick} />}
        {selected && (
          <InfoWindow
            position={selected}
            visible={!!selected}
            onClose={onCloseInfoWindow}
          >
            <Card className="map-info-card">
              <div className="map-info-address">{address}</div>
              <div className="map-info-actions">
                <a
                  href={mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-info-link"
                >
                  <Icon icon="mdi:open-in-new" width={16} height={16} />
                  <span>Open in Maps</span>
                </a>
              </div>
            </Card>
          </InfoWindow>
        )}
      </Map>
    </div>
  );
};

const ContractorLocationWrapper = (props: { user: any }) => {
  const GoogleMapsComponent = GoogleApiWrapper({
    apiKey: config.googleMapsApiKey,
  })((googleProps: { google?: any }) => (
    <ContractorLocationInner {...googleProps} user={props.user} />
  ));
  return <GoogleMapsComponent />;
};

export default ContractorLocationWrapper;
